import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Plus, Save, Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

const SOURCE_LABELS = {
  stima: "Stima", consulenza: "Consulenza", contatto: "Contatto", book: "Book", newsletter: "Newsletter",
  shop: "Shop", shop_preventivo: "Preventivo Shop", lead_pdf: "Lead PDF", shop_the_project: "Shop the Project", manuale: "Manuale",
};

export function CrmPanel() {
  const [data, setData] = useState({ items: [], statuses: [], status_labels: {}, sources: [] });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [marketing, setMarketing] = useState("");
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "", marketing_consent: false, notes_internal: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;
      if (source) params.source = source;
      if (marketing) params.marketing = marketing;
      const { data } = await api.get("/admin/crm/contacts", { params });
      setData(data);
    } catch { toast.error("Errore caricamento contatti"); }
  };
  useEffect(() => { load(); }, [status, source, marketing]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (c) => {
    setSel(c);
    try { const { data } = await api.get(`/admin/crm/contacts/${c.id}`); setDetail(data); }
    catch { toast.error("Errore caricamento scheda"); }
  };

  const patch = async (fields) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/crm/contacts/${sel.id}`, fields);
      setDetail((d) => ({ ...d, ...data })); setSel((s) => ({ ...s, ...data }));
      toast.success("Contatto aggiornato"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore aggiornamento"); }
    finally { setBusy(false); }
  };

  const create = async () => {
    if (!form.email.includes("@")) { toast.error("Email non valida"); return; }
    setBusy(true);
    try {
      await api.post("/admin/crm/contacts", form);
      toast.success("Contatto creato"); setShowNew(false);
      setForm({ name: "", surname: "", email: "", phone: "", marketing_consent: false, notes_internal: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore creazione"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Archiviare questo contatto? Le attività collegate (ordini, stime, prenotazioni) restano conservate.")) return;
    try { await api.delete(`/admin/crm/contacts/${sel.id}`); toast.success("Contatto archiviato"); setSel(null); setDetail(null); load(); }
    catch { toast.error("Errore archiviazione"); }
  };

  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  // ---------- Dettaglio contatto ----------
  if (sel && detail) {
    const d = detail;
    return (
      <div className="space-y-6" data-testid="crm-detail">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => { setSel(null); setDetail(null); }} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="crm-back">
            <ArrowLeft size={14} /> Tutti i contatti
          </button>
          <Select value={d.lead_status} onValueChange={(v) => patch({ lead_status: v })}>
            <SelectTrigger className="rounded-none w-48" data-testid="crm-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {data.statuses.map((s) => <SelectItem key={s} value={s}>{data.status_labels[s] || s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-hair p-5 md:p-6" data-testid="crm-profile">
            <div className="eyebrow mb-3">Profilo</div>
            <p className="font-serif text-2xl">{[d.name, d.surname].filter(Boolean).join(" ") || "—"}</p>
            <p className="text-sm text-taupe mt-1">{d.email}{d.phone ? ` · ${d.phone}` : ""}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(d.sources || []).map((s) => (
                <span key={s} className="text-[10px] tracking-[0.12em] uppercase border border-gold/50 text-gold px-2 py-0.5">{SOURCE_LABELS[s] || s}</span>
              ))}
            </div>
            <dl className="mt-4 space-y-1.5 text-xs text-taupe">
              <div className="flex justify-between"><dt>Acquisito il</dt><dd>{fmtDate(d.created_at)}</dd></div>
              <div className="flex justify-between"><dt>Privacy</dt><dd>{d.privacy_consent ? `Sì · ${fmtDate(d.privacy_consent_at)} · ${d.privacy_version || ""}` : "No"}</dd></div>
              <div className="flex justify-between"><dt>Marketing</dt><dd>{d.marketing_consent ? `Sì · ${fmtDate(d.marketing_consent_at)} · fonte ${SOURCE_LABELS[d.marketing_consent_source] || d.marketing_consent_source || "—"}` : "No"}</dd></div>
            </dl>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              {[["Stime", d.estimates_count], ["Consulenze", d.bookings_count], ["Ordini", d.orders_count], ["Book", d.downloads_count], ["Messaggi", d.messages_count], ["Valore", eur(d.orders_value)]].map(([l, v]) => (
                <div key={l} className="border border-hair p-2"><div className="text-[9px] tracking-[0.12em] uppercase text-taupe">{l}</div><div className="font-serif text-lg mt-0.5">{v}</div></div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-hair p-5 md:p-6 space-y-4" data-testid="crm-edit">
            <div className="eyebrow">Modifica</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome</Label>
                <Input defaultValue={d.name} onBlur={(e) => e.target.value !== d.name && patch({ name: e.target.value })} className="rounded-none mt-1" data-testid="crm-edit-name" /></div>
              <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Cognome</Label>
                <Input defaultValue={d.surname} onBlur={(e) => e.target.value !== d.surname && patch({ surname: e.target.value })} className="rounded-none mt-1" data-testid="crm-edit-surname" /></div>
            </div>
            <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Telefono</Label>
              <Input defaultValue={d.phone} onBlur={(e) => e.target.value !== d.phone && patch({ phone: e.target.value })} className="rounded-none mt-1" data-testid="crm-edit-phone" /></div>
            <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
              <input type="checkbox" checked={!!d.marketing_consent} onChange={(e) => patch({ marketing_consent: e.target.checked })} className="w-5 h-5 accent-[#9E8462]" data-testid="crm-edit-marketing" />
              Consenso marketing/newsletter attivo
            </label>
            <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Note interne</Label>
              <Textarea rows={3} defaultValue={d.notes_internal} onBlur={(e) => e.target.value !== d.notes_internal && patch({ notes_internal: e.target.value })} className="rounded-none mt-1" data-testid="crm-edit-notes" /></div>
            <button onClick={remove} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="crm-delete"><Trash2 size={14} /> Archivia contatto</button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-hair p-5 md:p-6" data-testid="crm-activity">
            <div className="eyebrow mb-3">Attività</div>
            {(d.estimates || []).map((e, i) => <p key={`e${i}`} className="text-xs text-taupe py-1.5 border-b border-hair/60">Stima {e.number} · {e.status} · {fmtDate(e.created_at)}</p>)}
            {(d.bookings || []).map((b, i) => <p key={`b${i}`} className="text-xs text-taupe py-1.5 border-b border-hair/60">Consulenza {b.date} {b.time_slot} · {b.consultation_type} · {b.status}</p>)}
            {(d.orders || []).map((o, i) => <p key={`o${i}`} className="text-xs text-taupe py-1.5 border-b border-hair/60">Ordine {o.order_number || "—"} · {eur(o.amount)} · {o.payment_status}</p>)}
            {(d.downloads || []).map((x, i) => <p key={`d${i}`} className="text-xs text-taupe py-1.5 border-b border-hair/60">Book “{x.book_title}” scaricato · {fmtDate(x.downloaded_at)}</p>)}
            {(d.shopping_lists || []).map((x, i) => <p key={`s${i}`} className="text-xs text-taupe py-1.5 border-b border-hair/60">Shopping List “{x.project_name}” · {x.status}</p>)}
            {(d.stp_requests || []).map((x, i) => <p key={`r${i}`} className="text-xs text-taupe py-1.5 border-b border-hair/60">Richiesta Shop the Project · {x.room_type || "—"} · {x.status} · {fmtDate(x.created_at)}</p>)}
            {(d.messages || []).map((m, i) => <p key={`m${i}`} className="text-xs text-taupe py-1.5 border-b border-hair/60">Messaggio: {m.subject || "—"} · {fmtDate(m.created_at)}</p>)}
            {![d.estimates, d.bookings, d.orders, d.downloads, d.shopping_lists, d.messages].some((a) => (a || []).length) && (
              <p className="text-xs text-taupe">Nessuna attività registrata.</p>
            )}
          </div>
          <div className="bg-white border border-hair p-5 md:p-6" data-testid="crm-history">
            <div className="eyebrow mb-3">Storico stati</div>
            {(d.status_history || []).slice().reverse().map((h, i) => (
              <p key={i} className="text-xs text-taupe py-2 border-b border-hair/60 last:border-0">
                {fmtDate(h.at)} — {data.status_labels[h.from] || h.from} → <strong className="text-ink">{data.status_labels[h.to] || h.to}</strong> · {h.by}
              </p>
            ))}
            {!(d.status_history || []).length && <p className="text-xs text-taupe">Nessun cambio di stato registrato.</p>}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Lista contatti ----------
  return (
    <div className="space-y-5" data-testid="crm-panel">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-taupe" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Cerca nome, email, telefono…" className="rounded-none pl-9" data-testid="crm-search" />
          </div>
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-none w-44" data-testid="crm-filter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {data.statuses.map((s) => <SelectItem key={s} value={s}>{data.status_labels[s] || s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={source || "all"} onValueChange={(v) => setSource(v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-none w-44" data-testid="crm-filter-source"><SelectValue placeholder="Origine" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le origini</SelectItem>
            {data.sources.map((s) => <SelectItem key={s} value={s}>{SOURCE_LABELS[s] || s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={marketing || "all"} onValueChange={(v) => setMarketing(v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-none w-44" data-testid="crm-filter-marketing"><SelectValue placeholder="Marketing" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Marketing: tutti</SelectItem>
            <SelectItem value="si">Consenso marketing</SelectItem>
            <SelectItem value="no">Senza consenso</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={load} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="crm-apply">Cerca</button>
        <button onClick={() => setShowNew(true)} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="crm-new"><Plus size={14} /> Nuovo</button>
      </div>

      {showNew && (
        <div className="bg-white border border-hair p-5 md:p-6 space-y-4" data-testid="crm-new-form">
          <div className="eyebrow">Nuovo contatto</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="crm-new-name" />
            <Input placeholder="Cognome" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className="rounded-none" data-testid="crm-new-surname" />
            <Input placeholder="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" data-testid="crm-new-email" />
            <Input placeholder="Telefono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" data-testid="crm-new-phone" />
          </div>
          <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
            <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="w-5 h-5 accent-[#9E8462]" data-testid="crm-new-marketing" />
            Consenso marketing espresso (facoltativo)
          </label>
          <div className="flex gap-2">
            <button onClick={create} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="crm-new-save"><Save size={14} /> Salva</button>
            <button onClick={() => setShowNew(false)} className={`${btn} border border-hair hover:border-ink`} data-testid="crm-new-cancel">Annulla</button>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="crm-list">
        {data.items.length === 0 && <div className="p-12 text-center text-taupe bg-white border border-hair">Nessun contatto trovato.</div>}
        {data.items.map((c) => (
          <button key={c.id} onClick={() => open(c)} className="w-full text-left bg-white border border-hair p-4 md:p-5 hover:border-gold transition-colors" data-testid={`crm-row-${c.id}`}>
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-lg">{[c.name, c.surname].filter(Boolean).join(" ") || c.email}</span>
                  <span className="text-[10px] tracking-[0.14em] uppercase border border-gold/50 text-gold px-2 py-0.5">{data.status_labels[c.lead_status] || c.lead_status}</span>
                  {c.marketing_consent && <span className="text-[10px] tracking-[0.14em] uppercase border border-emerald-700/40 text-emerald-700 px-2 py-0.5">Marketing</span>}
                </div>
                <p className="text-sm text-taupe mt-1 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""} · {(c.sources || []).map((s) => SOURCE_LABELS[s] || s).join(" · ")}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-taupe">{fmtDate(c.last_activity_at || c.created_at)}</p>
                <p className="text-sm mt-1">{c.orders_count > 0 ? eur(c.orders_value) : ""}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
